import { SignedSafetyControlRecord } from '../../services/reportsApi';

const RECORD_ID = 'adfe7e2c-9a95-4632-91fd-bfcd4880d68b';

/** Canonical Rev 5 illustrative signed body with a matching-revision technical annex. */
export const SIGNED_RECORD_PREVIEW_FIXTURE: SignedSafetyControlRecord = {
  record_id: RECORD_ID,
  revision: 5,
  lifecycle: 'signed',
  signed_at: '2026-07-29T20:45:33Z',
  signer: { label: 'Maya Chen · Site EHS Manager', state: 'recorded_signer' },
  record_provenance: {
    label: 'Illustrative demonstration · Not customer-site results',
    state: 'illustrative_demonstration',
  },
  identity: {
    condition_label: 'Forklift-Pedestrian Proximity',
    summary: 'Recurring pedestrian proximity at the walkway crossing.',
  },
  scope: {
    site_id: 'YYZ Distribution Centre',
    zone_id: 'Main walkway crossing',
    risk_type: 'near_miss',
    camera_id: 'Camera 04',
  },
  related: {
    safety_case_id: 'case-walkway-near-miss',
    workflow_id: 'near_miss|worksafety|cam_walkway_zone_demo|demo',
  },
  new_activity: {
    has_new_activity_since_report: true,
    newer_representative_incident_id: 'yyz-20260729-new-001',
  },
  history: {
    current_revision: 5,
    revisions: [
      { revision: 5, current: true, parent_revision: 4, signature_state: 'signed' },
      { revision: 4, current: false, parent_revision: 3, signature_state: 'signed' },
    ],
  },
  baseline: {
    rate: 18 / 42.5,
    window: { start: '2026-07-01T00:00:00Z', end: '2026-07-07T00:00:00Z' },
  },
  site_selected_control: {
    summary: 'Fixed pedestrian barrier installed at the main walkway crossing',
    status: 'verified',
    owner: 'Jordan Patel · Operations Manager',
    completed_at: '2026-07-07T18:00:00Z',
  },
  recheck: {
    rate: 7 / 39,
    window: { start: '2026-07-08T00:00:00Z', end: '2026-07-12T00:00:00Z' },
  },
  recheck_result: {
    label: 'Reviewed recurrence was lower in the comparable recheck window',
    state: 'decreased',
    completeness: 'comparable_complete',
    comparable: true,
    detail: 'The reviewed observation rate changed from 0.42 to 0.18 per usable hour after the site installed a pedestrian barrier at the walkway crossing. This is a 58% descriptive decrease across the two signed observation windows.',
  },
  signed_body: {
    identity: {
      record_id: RECORD_ID,
      revision: 5,
      lifecycle_state: 'signed',
      title: 'Forklift-Pedestrian Proximity',
      condition_type: 'near_miss',
      condition_summary: 'Recurring pedestrian proximity at the walkway crossing.',
      provenance: 'illustrative_demonstration',
      provenance_label: 'Illustrative demonstration · Not customer-site results',
    },
    governance: {
      signer_label: 'Maya Chen · Site EHS Manager',
      signer_state: 'recorded_signer',
      signer_name: 'Maya Chen',
      signer_role: 'Site EHS Manager',
      signer_provenance: 'maya.chen@customer.example',
      signed_at: '2026-07-29T20:45:33Z',
      prepared_by: 'Edgentik',
      prepared_for: 'Site EHS',
      confidentiality: 'Internal',
    },
    scope: {
      organization: 'Northstar Logistics',
      site: 'YYZ Distribution Centre',
      camera: 'Camera 04',
      zone: 'Main walkway crossing',
      policy: 'Forklift–pedestrian proximity review',
      observation_window_label: 'Signed review period · Jul 1–12, 2026',
    },
    finding: {
      summary: 'At the walkway crossing, 6 of 18 reviewed proximity observations occurred between 13:00 and 15:00, the highest observed rate at 0.80 per usable hour. This identifies one location and operating window for field review.',
      reviewed_episodes: 18,
      usable_hours: 42.5,
      rate_per_usable_hour: 18 / 42.5,
      coverage_percent: 95,
      peak_location: 'Main walkway crossing',
      peak_window: '13:00–15:00',
      peak_counts: '6 of 18 reviewed observations · 0.80 per usable hour',
      time_bands: [
        {
          window_label: '05:00–09:00',
          context: 'Early shift',
          reviewed_episodes: 3,
          usable_hours: 10,
          rate_per_usable_hour: 0.30,
        },
        {
          window_label: '09:00–13:00',
          context: 'Morning operations',
          reviewed_episodes: 4,
          usable_hours: 10,
          rate_per_usable_hour: 0.40,
        },
        {
          window_label: '13:00–15:00',
          context: 'Peak crossing activity',
          reviewed_episodes: 6,
          usable_hours: 7.5,
          rate_per_usable_hour: 0.80,
        },
        {
          window_label: '15:00–19:00',
          context: 'Afternoon operations',
          reviewed_episodes: 3,
          usable_hours: 10,
          rate_per_usable_hour: 0.30,
        },
        {
          window_label: '19:00–23:00',
          context: 'Evening shift',
          reviewed_episodes: 2,
          usable_hours: 5,
          rate_per_usable_hour: 0.40,
        },
      ],
    },
    control: {
      description: 'Fixed pedestrian barrier installed at the main walkway crossing',
      owner: 'Jordan Patel · Operations Manager',
      status: 'verified',
      implemented_at: '2026-07-07T18:00:00Z',
      verification_method: 'Field walk-through and anchor inspection',
      location: 'Main walkway crossing',
      field_verifier: 'Priya Singh · EHS Specialist',
      verified_at: '2026-07-08T14:30:00Z',
      implementation_evidence_reference: 'CTRL-EV-2026-071',
      supporting_control: 'Crossing checks added to the start-of-shift supervisor walk-through',
      notes: 'Barrier anchors and pedestrian path clearance verified.',
      actions: [
        {
          action: 'Install fixed pedestrian barrier at main walkway crossing',
          owner: 'Jordan Patel · Operations Manager',
          status: 'completed',
        },
      ],
    },
    recheck: {
      status: 'claimable',
      summary: 'Comparable follow-up after barrier implementation.',
      reviewed_episodes: 7,
      usable_hours: 39,
      rate_per_usable_hour: 7 / 39,
      coverage_percent: 91,
      window: 'After barrier',
      start: '2026-07-08T00:00:00Z',
      end: '2026-07-12T00:00:00Z',
      comparable: true,
      measurement_denominator_verified: true,
      comparability_checklist: [
        { label: 'Same camera', met: true },
        { label: 'Same defined zone', met: true },
        { label: 'Same proximity condition', met: true },
        { label: 'Same review method', met: true },
        { label: 'Same visibility rule', met: true },
        { label: 'Matched operating windows', met: true },
        { label: 'Baseline coverage 95%', met: true },
        { label: 'Recheck coverage 91%', met: true },
        { label: 'Denominator threshold met', met: true },
      ],
    },
    result: {
      label: 'decreased',
      headline: 'Reviewed recurrence was lower in the comparable recheck window',
      explanation: 'The reviewed observation rate changed from 0.42 to 0.18 per usable hour after the site installed a pedestrian barrier at the walkway crossing. This is a 58% descriptive decrease across the two signed observation windows.',
      absolute_rate_change: (7 / 39) - (18 / 42.5),
      descriptive_percent_change: 100 * ((7 / 39) - (18 / 42.5)) / (18 / 42.5),
      comparability_met: true,
      supports: ['Comparison requirements met across the two signed observation windows.'],
      does_not_establish: ['This comparison does not establish that the barrier caused the decrease, prevented an incident or changed overall site risk.'],
    },
    next_decision: {
      decision: 'Retain the barrier and continue routine verification.',
    },
    evidence: [
      {
        evidence_id: 'EV-REV5-001',
        title: 'Walkway crossing representative observation',
        metadata: {
          timestamp: '2026-07-06T13:18:00Z',
          camera_id: 'cam_walkway_zone_demo',
          zone_id: 'worksafety',
          review_status: 'Reviewed representative',
        },
        snapshot_ref: null,
        clip_ref: null,
        illustrative: true,
      },
      {
        evidence_id: 'EV-REV5-002',
        title: 'Barrier approach representative observation',
        metadata: {
          timestamp: '2026-07-06T13:42:00Z',
          camera_id: 'cam_walkway_zone_demo',
          zone_id: 'worksafety',
          review_status: 'Reviewed representative',
        },
        snapshot_ref: null,
        clip_ref: null,
        illustrative: true,
      },
      {
        evidence_id: 'EV-REV5-003',
        title: 'Peak-window representative observation',
        metadata: {
          timestamp: '2026-07-06T14:11:00Z',
          camera_id: 'cam_walkway_zone_demo',
          zone_id: 'worksafety',
          review_status: 'Reviewed representative',
        },
        snapshot_ref: null,
        clip_ref: null,
        illustrative: true,
      },
    ],
    technical_annex: {
      available: true,
      source_revision_matches: true,
      measurement_assurance: {
        status: 'Measurement inputs retained for signed revision 5',
        baseline: '18 reviewed observations · 42.5 usable hours · 0.42 per usable hour',
        recheck: '7 reviewed observations · 39 usable hours · 0.18 per usable hour',
        denominator_source: 'Usable monitored hours retained in the signed review',
      },
      convergence: {
        status: 'Review complete for the bounded signed comparison',
        baseline_reviewed_episodes: 18,
        recheck_reviewed_episodes: 7,
      },
      coverage: {
        baseline: '42.5 usable hours · 95% coverage',
        recheck: '39 usable hours · 91% coverage',
      },
      scan_review: {
        status: 'Complete',
        retained_baseline_episodes: 18,
        retained_recheck_episodes: 7,
      },
      walkdown: {
        status: 'Completed',
        method: 'Supervisor walk-through',
        control: 'Pedestrian barrier at the walkway crossing',
      },
      provenance: {
        source_revision: 5,
        record_state: 'Signed',
        source: 'Illustrative demonstration · Not customer-site results',
      },
    },
  },
  detail: {
    executive_summary: null,
    document_control: null,
    condition_scope: { condition: null, scope: {}, observation_window: null },
    baseline: { detail: null, window: null, metrics: {} },
    site_selected_control: { detail: null, comparison: null, action_plan: null },
    recheck: { detail: null, window: null, metrics: {}, comparability: null },
    impact_measurement: { graph: null, visuals: [] },
    scan_report: null,
    evidence: { gallery: null, cards: [], total_items: 0 },
    limitations_claim_boundaries: {
      limitations: null,
      claims: null,
      claim_boundary: null,
      claim_language_policy: null,
    },
  },
  exports: { pdf_available: false, pdf_mode: null },
};
